import React from "react"

import { ImageWithFallback } from "~/src/components"
import { CubeIcon, FolderPlusIcon, HeartIcon, NoSymbolIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"

export type CatalogProductTileProps = {
  id: number
  slug: string
  name: string
  lowPrice: number
  loved: boolean
  hidden: boolean
  photoUrl: string
  productUrl: string
  lastOrderedAt?: string
  className?: string
  onLove?: (id: number, slug: string) => void
  onHide?: (id: number, slug: string) => void
  onAddToProjectRequest?: (id: number, slug: string) => void
}

export function CatalogProductTile(props: CatalogProductTileProps) {
  const {
    id,
    slug,
    name,
    lowPrice,
    className,
    loved = false,
    hidden = false,
    photoUrl,
    productUrl,
    lastOrderedAt,
    onLove,
    onHide,
    onAddToProjectRequest,
  } = props

  const heartIconColors = loved
    ? "bg-pink-200 text-red-600 hover:bg-white hover:fill-white"
    : "bg-white text-gray-500 hover:bg-pink-200 hover:text-red-600"
  const hiddenIconColors = hidden
    ? "bg-gray-500 text-white hover:text-slate-600 hover:bg-white"
    : "bg-white text-gray-500 hover:text-white hover:bg-gray-500"

  return (
    <div
      className={twMerge(
        "flex flex-col w-full h-full border rounded-lg bg-white gap-2 relative overflow-hidden",
        className
      )}
    >
      <div className="absolute flex flex-col gap-1 z-10 p-2 top-0 right-0 ">
        <div
          className={`group rounded-xl p-1 ${heartIconColors} hover:cursor-pointer`}
          onClick={() => onLove?.(id, slug)}
        >
          <HeartIcon
            className={`h-4 w-4 ${loved ? "fill-red-600 group-hover:fill-white group-hover:text-slate-600" : "group-hover:fill-red-600"}`}
          />
        </div>
        <div className={`rounded-xl p-1 ${hiddenIconColors} hover:cursor-pointer`} onClick={() => onHide?.(id, slug)}>
          <NoSymbolIcon className={`h-4 w-4`} />
        </div>
      </div>

      <a href={productUrl} className="p-0 m-0">
        <ImageWithFallback
          src={photoUrl}
          className="flex h-40"
          imgProps={{ className: "w-full object-contain object-center" }}
        >
          <div className="h-full w-full flex items-center justify-center text-gray-400 bg-gray-100">
            <CubeIcon className="h-10 w-10 stroke-1" />
          </div>
        </ImageWithFallback>
      </a>
      <hgroup className="grow px-4 p-1 sm:pt-3">
        <h1 className="font-medium text-gray-900">{name}</h1>
        <h2 className="text-gray-500 text-sm">From ${lowPrice}</h2>
      </hgroup>

      <div className="justify-bottom">
        {lastOrderedAt && (
          <div className="bg-slate-100 text-center">
            <h2 className="text-xs p-2">Last ordered: {format(lastOrderedAt, "yyyy-MM-dd")}</h2>
          </div>
        )}

        <div
          className="border-t p-2 text-blue-600 hover:cursor-pointer"
          onClick={() => onAddToProjectRequest?.(id, slug)}
        >
          <h2 className="flex gap-2 items-center justify-center text-sm max-sm:hidden">
            <FolderPlusIcon className="h-4 w-4" />
            Add to project request
          </h2>
          <h2 className="flex gap-2 justify-center text-sm sm:hidden">
            <FolderPlusIcon className="h-4 w-4" />
            Add to project
          </h2>
        </div>
      </div>
    </div>
  )
}
